import React, { useState } from "react";
import "./testing_tool_issue_accordion.css";

function normalizeSeverity(value) {
    return String(value || "").trim().toLowerCase();
}

function toTitleCase(value) {
    if (!value) return "";
    const text = String(value);
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

function getEffortValue(item) {
    if (!item) return "Low";
    if (typeof item.effort === "string") return item.effort;
    if (item.effort && typeof item.effort === "object") {
        return item.effort.level || "Low";
    }
    if (typeof item.effort_level === "string") return item.effort_level;
    return "Low";
}

export default function TestingToolIssueAccordion({ items, sectionLabel, onToggleResolved }) {
    const safeItems = Array.isArray(items) ? items : [];
    const resolveItemId = (item, index) => item?.id ?? item?.issue_id ?? `${sectionLabel}-${index}`;
    const firstItemId = safeItems.length > 0 ? resolveItemId(safeItems[0], 0) : null;
    const [openItemId, setOpenItemId] = useState(firstItemId);

    if (safeItems.length === 0) {
        return null;
    }

    const findingCount = safeItems.length;
    const findingText = `${findingCount} FINDING${findingCount === 1 ? "" : "S"}`;

    return (
        <section className="testing_tool_accordion">
            <div className="testing_tool_accordion-head">
                <h3 className="testing_tool_accordion-section">{sectionLabel}</h3>
                <span className="testing_tool_accordion-count">{findingText}</span>
                <span className="testing_tool_accordion-head-line" aria-hidden />
            </div>

            {safeItems.map((item, index) => {
                const itemId = resolveItemId(item, index);
                const isOpen = openItemId === itemId;
                const severity = toTitleCase(item.severity || "Low");
                const normalizedSeverity = normalizeSeverity(item.severity);
                const effortValue = getEffortValue(item);
                const normalizedEffort = normalizeSeverity(effortValue);
                const isNavigationSection = String(sectionLabel || "").toLowerCase() === "navigation";
                const issueIcon = item.icon;
                const isIconComponent = typeof issueIcon === "function";
                const isIconImage =
                    typeof issueIcon === "string" &&
                    (issueIcon.endsWith(".svg") ||
                        issueIcon.endsWith(".png") ||
                        issueIcon.endsWith(".jpg") ||
                        issueIcon.endsWith(".webp"));
                const navigationIconByEffort = {
                    high: "/img/testing-tool/Navigation_High.svg",
                    medium: "/img/testing-tool/Navigation_Medium.svg",
                    low: "/img/testing-tool/Navigation_Low.svg",
                };
                const iconSource = isNavigationSection
                    ? navigationIconByEffort[normalizedEffort] || navigationIconByEffort.low
                    : isIconImage
                        ? issueIcon
                        : null;

                return (
                    <article
                        key={itemId}
                        className={`testing_tool_accordion-item${isOpen ? " is-open" : ""}`}
                    >
                        <button
                            type="button"
                            className="testing_tool_accordion-trigger"
                            aria-expanded={isOpen}
                            onClick={() => setOpenItemId(isOpen ? null : itemId)}
                        >
                            <div className="testing_tool_accordion-trigger-main">
                                <span className={`testing_tool_accordion-icon testing_tool_accordion-icon--${normalizedEffort}`}>
                                    {isNavigationSection ? (
                                        <img src={iconSource} alt="" aria-hidden className="testing_tool_accordion-icon-image" />
                                    ) : isIconComponent ? (
                                        React.createElement(issueIcon, { className: "testing_tool_accordion-icon-image" })
                                    ) : iconSource ? (
                                        <img src={iconSource} alt="" aria-hidden className="testing_tool_accordion-icon-image" />
                                    ) : (
                                        <span className="testing_tool_accordion-icon-fallback">
                                            {String(sectionLabel || "").charAt(0)}
                                        </span>
                                    )}
                                </span>
                                <div className="testing_tool_accordion-trigger-content">
                                    <div className="testing_tool_accordion-trigger-top">
                                        <p className="testing_tool_accordion-title">
                                            {item.title || item.issue_title}
                                        </p>
                                        <span className={`testing_tool_accordion-severity testing_tool_accordion-severity--${normalizedSeverity}`}>
                                            {severity} severity
                                        </span>
                                        {item.resolved && (
                                            <button
                                                type="button"
                                                className="testing_tool_accordion-resolvetag is-resolved"
                                                onClick={() => onToggleResolved?.(itemId)}
                                            >
                                                <img
                                                    src="/img/testing-tool/resolvedTag.svg"
                                                    alt="Resolved Tag"
                                                    aria-hidden
                                                    className="testing_tool_accordion-resolvetag-icon"
                                                />
                                                Resolved
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="testing_tool_accordion-trigger-meta">
                                <div className="testing_tool_accordion-effort-wrapper">
                                    <span className="testing_tool_accordion-effort-label">EFFORT</span>
                                    <span className={`testing_tool_accordion-effort-value testing_tool_accordion-effort-value--${normalizedEffort}`}>
                                        {effortValue}
                                    </span>
                                </div>
                                <span className={`testing_tool_accordion-chevron${isOpen ? " is-open" : ""}`}>
                                    <img src="/img/testing-tool/arrow-down-round.svg" alt="" aria-hidden />
                                </span>
                            </div>
                        </button>

                        <div
                            className={`testing_tool_accordion-body${isOpen ? " is-open" : ""}`}
                            aria-hidden={!isOpen}
                        >
                            <div className="testing_tool_accordion-content-inner">
                                <div className="testing_tool_accordion-content">
                                    <div className="testing_tool_accordion-col">
                                        {(item.location || item.found_on) ? (
                                            <>
                                                <p className="testing_tool_accordion-kicker">LOCATION</p>
                                                <p className="testing_tool_accordion-trigger-description">{item.location || item.found_on}</p>
                                                {item.page_url ? (
                                                    <a
                                                        href={item.page_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="testing_tool_accordion-location-link"
                                                    >
                                                        {item.page_url}
                                                    </a>
                                                ) : null}
                                            </>
                                        ) : null}

                                        {(item.details || item.issue || item.user_problem) ? (
                                            <>
                                                <p className={`testing_tool_accordion-kicker${(item.location || item.found_on) ? " pt-24" : ""}`}>THE ISSUE</p>
                                                <p className="testing_tool_accordion-trigger-description">{item.details || item.issue || item.user_problem}</p>
                                            </>
                                        ) : null}

                                        {(item.impact || item.potentialImpact) ? (
                                            <>
                                                <p className="testing_tool_accordion-kicker pt-24">IMPACT</p>
                                                <p className="testing_tool_accordion-trigger-description">{item.impact || item.potentialImpact}</p>
                                            </>
                                        ) : null}
                                    </div>

                                    <div className="testing_tool_accordion-col">
                                        {item.evidence ? (
                                            <>
                                                <p className="testing_tool_accordion-kicker">RELATED CONTENT</p>
                                                <p className="testing_tool_accordion-trigger-description">{item.evidence}</p>
                                            </>
                                        ) : null}

                                        {(item.solution || item.suggestedSolution || item.recommendation) ? (
                                            <div className={`testing_tool_accordion-solution-wrapper${item.evidence ? " pt-24" : ""}`}>
                                                <p className="testing_tool_accordion-kicker testing_tool_accordion-kicker--flex testing_tool_accordion-kicker--pink">
                                                    <img src="/img/testing-tool/SuggestedSolution.svg" alt="Suggested Solution" />
                                                    SUGGESTED SOLUTION
                                                </p>
                                                <div className="testing_tool_accordion-solution">
                                                    "{item.solution || item.suggestedSolution || item.recommendation}"
                                                </div>
                                            </div>
                                        ) : null}

                                        <div className="testing_tool_accordion-actions">
                                            <button
                                                type="button"
                                                className={`testing_tool_accordion-resolve${item.resolved ? " is-resolved" : ""}`}
                                                onClick={() => onToggleResolved?.(itemId)}
                                            >
                                                {item.resolved ? (
                                                    <img src="/img/testing-tool/Resolved.svg" alt="Resolved" aria-hidden />
                                                ) : (
                                                    <img src="/img/testing-tool/urlIcon.svg" alt="Mark as resolved" aria-hidden />
                                                )}
                                                {item.resolved ? "Resolved" : "Mark as resolved"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </article>
                );
            })}
        </section>
    );
}
